import { useMemo } from "react";

import { EDITOR_CONSTANTS } from "@perceptron/constants/editorConstants";
import { MediaSetContext } from "@perceptron/utils/types";
import { Editor } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";

import { useCaretBubbleStore } from "@demo/store/caret-bubble";
import { Role } from "@demo/types/base";
import { handleFileDropToExistingMediaSetContainer } from "@demo/utils/file-handler";

import { CaretBubbleExtension, CaretBubblePosition } from "./caret-bubble";
import { ClipboardGuardExtension } from "./clipboard-guard";
import { DeleteTopBlankLineExtension } from "./delete-top-blank-line";
import { EditorPlaceholderExtension } from "./editor-placeholder";
import { fileHandlerExtension } from "./file-handler";
import { MediaSetContainerExtension } from "./media-set-container";
import { TrailingNodeExtension } from "./trailing-node";

interface EditorExtensionsConfig {
  context: MediaSetContext;
  placeholder?: string;
  role: Role;
  onCaretPositionChange?: (position: CaretBubblePosition) => void;
}

/**
 * Hook to create and configure all editor extensions
 * Single responsibility: Configure and return editor extensions
 */
export function useEditorExtensions({
  context,
  placeholder = "",
  role,
  onCaretPositionChange,
}: EditorExtensionsConfig) {
  return useMemo(
    () => [
      // StarterKit ships with the History extension (Cmd+Z / Cmd+Shift+Z).
      // Programmatic content swaps (clear, template load, store sync) are
      // excluded from the undo stack via the addToHistory meta at their call
      // sites, so undo only ever traverses the user's own edits.
      StarterKit,
      ClipboardGuardExtension.configure({
        context,
      }),
      EditorPlaceholderExtension.configure({
        placeholder,
        showOnlyWhenEditable: true,
        showOnlyCurrent: true,
        includeChildren: false,
      }),
      // Note: no @tiptap/extension-image — nothing in the app produces TipTap
      // `image` nodes (uploads go through mediaSetContainer), and registering
      // it let HTML-pasted <img> create phantom nodes that rendered in the
      // editor but were silently dropped from the model request.
      MediaSetContainerExtension.configure({
        context,
        role,
        onFileDrop: async (files: File[], mediaSetId: string, editor: Editor) => {
          // Use the role from editor config - we know which editor this is
          await handleFileDropToExistingMediaSetContainer(editor, files, mediaSetId);
        },
      }),
      CaretBubbleExtension.configure({
        onCaretPositionChange,
        setMousePosition: (position) => {
          useCaretBubbleStore.getState().setMousePosition(position);
        },
      }),
      TrailingNodeExtension,
      DeleteTopBlankLineExtension.configure({
        triggerNodeTypes: [EDITOR_CONSTANTS.NODE_TYPES.MEDIA_SET_CONTAINER],
      }),
      fileHandlerExtension({
        context,
        role,
      }),
    ],
    [placeholder, role, onCaretPositionChange, context],
  );
}
